// NEW_FILE_JUSTIFIED: Step 3 of claim wizard — document upload + AI extraction modal, extracted to stay under 200 lines
import { useRef, useState, useCallback } from 'react';
import { useMutation, useAction } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: components in src/components/insurance/ require three levels up to reach convex/_generated/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import pattern
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, ArrowRight, Upload, FileText, Save, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { IClaimFormState } from '@/hooks/useClaimForm';
import type { IClaimAiAnalysis } from '@/@types/claims';

// LOCAL_TYPE_JUSTIFIED: step-internal props, not reused outside wizard
interface IClaimWizardStepDocsProps {
  state: IClaimFormState;
  onAddDoc: (doc: IClaimFormState['uploadedDocs'][number]) => void;
  onPrefillFromAi: (data: IClaimAiAnalysis) => void;
  onNext: () => void;
  onPrev: () => void;
  onSaveDraft?: () => void;
  isSavingDraft?: boolean;
}

// LOCAL_TYPE_JUSTIFIED: AI modal state — ephemeral, not persisted
interface IAiModalState {
  open: boolean;
  storageId: string;
  isLoading: boolean;
  result: IClaimAiAnalysis | null;
  editableResult: IClaimAiAnalysis | null;
}

const AI_MODAL_INITIAL: IAiModalState = {
  open: false,
  storageId: '',
  isLoading: false,
  result: null,
  editableResult: null,
};

export function ClaimWizardStepDocs({
  state,
  onAddDoc,
  onPrefillFromAi,
  onNext,
  onPrev,
  onSaveDraft,
  isSavingDraft,
}: IClaimWizardStepDocsProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [aiModal, setAiModal] = useState<IAiModalState>(AI_MODAL_INITIAL);

  const generateUploadUrl = useMutation(api.storage.createUploadUrl);
  const extractClaimData = useAction(api.insurance.extractClaimDocument.extractClaimData);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setIsUploading(true);
      try {
        const uploadUrl = await generateUploadUrl();
        const res = await fetch(uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': file.type },
          body: file,
        });
        if (!res.ok) throw new Error('Upload failed');
        const { storageId } = (await res.json()) as { storageId: string }; // AS_JUSTIFIED: fetch response JSON narrowed to known Convex storage upload shape
        onAddDoc({
          storageId,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          documentType: 'other',
        });
        toast.success(`${file.name} uploaded`);
      } catch {
        toast.error('Upload failed. Please try again.');
      } finally {
        setIsUploading(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    },
    [generateUploadUrl, onAddDoc]
  );

  const handleAnalyze = useCallback(
    async (storageId: string) => {
      setAiModal({ open: true, storageId, isLoading: true, result: null, editableResult: null });
      try {
        const result = await extractClaimData({ storageId: storageId as Id<'_storage'> }); // AS_JUSTIFIED: storageId from Convex upload response narrowed to typed Id
        const analysis = result as IClaimAiAnalysis; // AS_JUSTIFIED: Convex action return narrowed to typed interface matching return validator
        setAiModal(prev => ({
          ...prev,
          isLoading: false,
          result: analysis,
          editableResult: analysis,
        }));
      } catch {
        toast.error('AI extraction failed. Please try again.');
        setAiModal(AI_MODAL_INITIAL);
      }
    },
    [extractClaimData]
  );

  const handleApplyAi = useCallback(() => {
    if (aiModal.editableResult) {
      onPrefillFromAi(aiModal.editableResult);
      toast.success('Form pre-filled from document');
    }
    setAiModal(AI_MODAL_INITIAL);
  }, [aiModal.editableResult, onPrefillFromAi]);

  const updateEditableField = useCallback((field: keyof IClaimAiAnalysis, value: string) => {
    setAiModal(prev => ({
      ...prev,
      editableResult: prev.editableResult ? { ...prev.editableResult, [field]: value } : null,
    }));
  }, []);

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Upload supporting documents (receipts, prescriptions, invoices). You can use AI to extract
        data from each document.
      </p>
      <div
        className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border p-8 transition-colors hover:bg-muted/40"
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm font-medium">Click to upload a document</p>
        <p className="text-xs text-muted-foreground">PDF, JPG, PNG supported</p>
        {isUploading && <Loader2 className="mt-2 h-5 w-5 animate-spin text-primary" />}
      </div>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept="application/pdf,image/*"
        onChange={e => void handleFileChange(e)}
      />
      {state.uploadedDocs.length > 0 && (
        <div className="space-y-2">
          {state.uploadedDocs.map(doc => (
            <div
              key={doc.storageId}
              className="flex items-center justify-between gap-3 rounded-lg border px-4 py-3"
            >
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">{doc.fileName}</p>
                  <p className="text-xs text-muted-foreground">
                    {(doc.fileSize / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => void handleAnalyze(doc.storageId)}>
                <Sparkles className="mr-1 h-4 w-4" /> Analyze
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between pt-2">
        <Button variant="outline" onClick={onPrev}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <div className="flex items-center gap-2">
          {onSaveDraft && (
            <Button variant="outline" onClick={onSaveDraft} disabled={isSavingDraft}>
              <Save className="mr-2 h-4 w-4" /> Save Draft
            </Button>
          )}
          <Button onClick={onNext} data-testid="claim-wizard-next">
            Next <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
      <Dialog open={aiModal.open} onOpenChange={open => !open && setAiModal(AI_MODAL_INITIAL)}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>AI Extraction Results</DialogTitle>
          </DialogHeader>
          {aiModal.isLoading && (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Analyzing document…</p>
            </div>
          )}
          {!aiModal.isLoading && aiModal.editableResult && (
            <div className="grid gap-3 text-sm">
              {(
                [
                  ['treatmentType', 'Treatment Type'],
                  ['dateOfService', 'Date of Service'],
                  ['providerName', 'Provider Name'],
                  ['providerAddress', 'Provider Address'],
                  ['diagnosis', 'Diagnosis'],
                  ['symptoms', 'Symptoms'],
                ] as [keyof IClaimAiAnalysis, string][]
              ).map(
                (
                  [field, label] // AS_JUSTIFIED: inline tuple literal narrowed to typed pair for map iteration
                ) => (
                  <div key={field} className="space-y-1">
                    <Label>{label}</Label>
                    {field === 'diagnosis' || field === 'symptoms' ? (
                      <Textarea
                        rows={2}
                        value={(aiModal.editableResult?.[field] as string) ?? ''} // AS_JUSTIFIED: IClaimAiAnalysis field value narrowed to string for input binding
                        onChange={e => updateEditableField(field, e.target.value)}
                      />
                    ) : (
                      <Input
                        value={(aiModal.editableResult?.[field] as string) ?? ''} // AS_JUSTIFIED: IClaimAiAnalysis field value narrowed to string for input binding
                        onChange={e => updateEditableField(field, e.target.value)}
                      />
                    )}
                  </div>
                )
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiModal(AI_MODAL_INITIAL)}>
              Cancel
            </Button>
            <Button onClick={handleApplyAi} disabled={aiModal.isLoading}>
              Apply to Form
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
